/**
 * LeadConversation — read-only WhatsApp conversation card (V1 Phase B).
 *
 * Renders the lead's WhatsApp thread from the Phase A read endpoint
 * (GET /leads/{id}/whatsapp/messages). Admin-only: the PARENT decides
 * whether to mount it (LeadDetailPage gates on the real role), and the
 * backend enforces the same gate server-side.
 *
 * PRIVACY by construction: message bodies live only in this component's
 * local state — never in shared lead state, never in localStorage, never
 * logged. Provider ids are not rendered.
 *
 * Polling: modest (12s) and only while the tab is visible
 * (document.visibilityState) — the app has no other polling surface, so
 * this stays deliberately conservative. Poll fetches the NEWEST page and
 * merges by message_id, preserving any older pages the user loaded.
 *
 * Composer (Phase C): free-form text only, enabled while the 24h
 * customer-service window appears open (display heuristic — the backend
 * is authoritative and a 409 template_required from it closes the
 * composer). One client_request_id per send attempt: the backend
 * guarantees a given id can never reach the provider twice, so a
 * double-click or browser retry is harmless. Uncertain outcomes (504)
 * keep the typed text and tell the operator NOT to resend.
 *
 * "Insertar mensaje sugerido": Dolce CRM suggestions from the shared
 * message-templates catalog (NOT Meta-approved WhatsApp templates — no
 * provider semantics). Selecting one only fills/appends the textarea;
 * it never sends and it lives inside the open-window branch, so it can
 * never bypass the 24h rule. Catalog is fetched lazily on first open.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  generateLeadAiReply, getLeadWhatsAppMessages, getWhatsAppMetaTemplates,
  markLeadWhatsAppRead, retryLeadWhatsAppMedia, sendLeadWhatsAppMessage,
  sendLeadWhatsAppTemplate,
} from "@/lib/whatsappApi";
import { useAuth } from "@/lib/auth";
import { API } from "@/lib/api";
import { normalizeStatus } from "@/lib/leadStatuses";
import { renderWhatsappTemplate } from "@/lib/bulkWhatsapp";
import { useMessageTemplates } from "@/hooks/useMessageTemplates";
import { orderTemplatesForStage } from "@/lib/messageTemplatesApi";
import {
  ArrowsClockwise, CaretDown, CaretUp, ChatCircleDots, ChatText, Check,
  Checks, DeviceMobile, FileText, PaperPlaneRight, Sparkle, WarningCircle,
  WhatsappLogo,
} from "@phosphor-icons/react";

const POLL_MS = 12000;
const PAGE_SIZE = 50;
const MAX_TEXT = 4096;
const WINDOW_MS = 24 * 60 * 60 * 1000;

/** One id per send ATTEMPT — the backend guarantees a given id can
 * never trigger the provider twice. randomUUID has full support in the
 * app's browser targets; the fallback only covers exotic embeds. */
function newClientRequestId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `crid_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

const TYPE_PLACEHOLDER = {
  image: "Imagen",
  document: "Documento",
  audio: "Audio",
  unsupported: "Contenido no compatible",
};

/** Etiquetas ES de los motivos canónicos de revisión humana del Copilot. */
const AI_REVIEW_REASON_LABELS = {
  commercial_negotiation: "condiciones comerciales",
  financial_claim: "información financiera",
  legal_claim: "información legal/fiscal",
  missing_information: "falta información",
  sensitive_commitment: "compromiso sensible",
  unsupported_project_claim: "dato del proyecto sin respaldo",
};

function aiReviewText(reasons) {
  const labels = (reasons || [])
    .map((r) => AI_REVIEW_REASON_LABELS[r])
    .filter(Boolean);
  return labels.length
    ? `Revisar antes de enviar: ${labels.join(", ")}.`
    : "Revisar antes de enviar.";
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function mergeById(existing, incoming) {
  // Union by message_id, then chronological (sent_at, message_id) — the
  // same compound order the backend paginates on, so poll results and
  // older pages interleave without duplicates or reordering glitches.
  const byId = new Map();
  for (const m of existing) byId.set(m.message_id, m);
  for (const m of incoming) byId.set(m.message_id, m);
  return [...byId.values()].sort((a, b) =>
    (a.sent_at || "").localeCompare(b.sent_at || "") ||
    (a.message_id || "").localeCompare(b.message_id || ""));
}

/** Delivery ticks — Dolce visual language, deliberately not WhatsApp's.
 * dolce_crm outbound always shows its state; mobile_app echoes only
 * show one once a REAL provider status arrived (their initial 'sent'
 * is our own persist-time baseline, not a delivery confirmation). */
function StatusTicks({ msg }) {
  if (msg.direction !== "outbound") return null;
  const s = msg.status;
  if (msg.origin === "mobile_app" && !["delivered", "read", "failed"].includes(s)) return null;
  if (s === "failed") {
    return (
      <span className="inline-flex items-center gap-0.5 text-red-600 dark:text-red-400" data-testid={`wa-msg-${msg.message_id}-status`}>
        <WarningCircle size={12} weight="bold" /> No entregado
      </span>
    );
  }
  if (s === "read") {
    return <Checks size={13} weight="bold" className="text-emerald-600 dark:text-emerald-400" data-testid={`wa-msg-${msg.message_id}-status`} />;
  }
  if (s === "delivered") {
    return <Checks size={13} className="text-stone-400" data-testid={`wa-msg-${msg.message_id}-status`} />;
  }
  if (s === "sent") {
    return <Check size={12} className="text-stone-400" data-testid={`wa-msg-${msg.message_id}-status`} />;
  }
  return null;
}

/** Inbound media block. Stored files render through the authenticated
 * /api/files endpoint (cookie auth — AuthImg pattern); every other
 * state shows a fixed Spanish notice. No provider ids surface here. */
function MediaBlock({ msg, onRetryMedia, retrying }) {
  const media = msg.media;
  if (!media) return null;
  const status = media.status;
  const fileUrl = media.file_id ? `${API}/files/${media.file_id}` : null;

  if (status === "stored" && fileUrl) {
    if (msg.message_type === "image") {
      return (
        <a href={fileUrl} target="_blank" rel="noopener noreferrer" data-testid={`wa-msg-${msg.message_id}-media`}>
          <img
            src={fileUrl}
            crossOrigin="use-credentials"
            alt={msg.text || "Imagen recibida"}
            className="max-h-48 rounded-md border border-stone-200 dark:border-stone-700 mb-1"
          />
        </a>
      );
    }
    if (msg.message_type === "document") {
      return (
        <a
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm underline text-stone-700 dark:text-stone-200 mb-1"
          data-testid={`wa-msg-${msg.message_id}-media`}
        >
          <FileText size={15} /> {media.filename || "Documento"}
        </a>
      );
    }
    if (msg.message_type === "audio") {
      return (
        <audio
          controls
          preload="metadata"
          crossOrigin="use-credentials"
          src={fileUrl}
          className="max-w-full mb-1"
          data-testid={`wa-msg-${msg.message_id}-media`}
        />
      );
    }
  }
  if (status === "external" && msg.message_type === "document") {
    // Documento de sistema (el dossier) enviado por enlace: el fichero vive
    // en Activos comerciales, no en /api/files, así que aquí no hay descarga.
    return (
      <div
        className="inline-flex items-center gap-1.5 text-sm text-stone-700 dark:text-stone-200 mb-1"
        data-testid={`wa-msg-${msg.message_id}-media`}
      >
        <FileText size={15} /> {media.filename || "Documento"}
      </div>
    );
  }
  if (status === "pending") {
    return <div className="text-xs italic text-stone-400 mb-0.5">Procesando archivo…</div>;
  }
  if (status === "too_large") {
    return <div className="text-xs italic text-stone-500 dark:text-stone-400 mb-0.5">Archivo demasiado grande para almacenar en Dolce.</div>;
  }
  if (status === "download_failed") {
    return (
      <div className="text-xs italic text-stone-500 dark:text-stone-400 mb-0.5 flex items-center gap-2">
        No se pudo guardar el archivo.
        <button
          type="button"
          onClick={() => onRetryMedia(msg)}
          disabled={retrying}
          className="not-italic underline text-stone-600 dark:text-stone-300 disabled:opacity-50"
          data-testid={`wa-msg-${msg.message_id}-media-retry`}
        >
          {retrying ? "Reintentando…" : "Reintentar"}
        </button>
      </div>
    );
  }
  return null; // unsupported → generic placeholder handles the label
}

function Bubble({ msg, onRetryMedia, retryingId }) {
  const inbound = msg.direction === "inbound";
  const fromMobile = msg.origin === "mobile_app";
  const stored = msg.media?.status === "stored";
  // Generic type label only while there is nothing real to render.
  const placeholder = stored ? null : TYPE_PLACEHOLDER[msg.message_type];
  return (
    <div
      className={`flex ${inbound ? "justify-start" : "justify-end"}`}
      data-testid={`wa-msg-${msg.message_id}`}
    >
      <div
        className={`max-w-[80%] rounded-lg px-3 py-2 text-sm border ${
          inbound
            ? "bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-700 text-stone-800 dark:text-stone-100"
            : "bg-[#fbf3ee] dark:bg-stone-800 border-[#e4cdbf] dark:border-stone-600 text-stone-800 dark:text-stone-100"
        }`}
      >
        {placeholder && (
          <div className="text-xs font-medium text-stone-500 dark:text-stone-400 italic mb-0.5">
            {placeholder}
          </div>
        )}
        {msg.message_type === "template" && (
          <div
            className="text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500 mb-0.5"
            data-testid={`wa-msg-${msg.message_id}-template-hint`}
          >
            Plantilla de WhatsApp
          </div>
        )}
        <MediaBlock msg={msg} onRetryMedia={onRetryMedia} retrying={retryingId === msg.message_id} />
        {msg.text && <div className="whitespace-pre-wrap break-words">{msg.text}</div>}
        {!msg.text && !placeholder && !msg.media && (
          <div className="text-xs text-stone-400 italic">—</div>
        )}
        <div className="flex items-center gap-1.5 mt-1 text-[10px] text-stone-400 dark:text-stone-500">
          <span>{fmtTime(msg.sent_at)}</span>
          <StatusTicks msg={msg} />
          {fromMobile && !inbound && (
            <span className="inline-flex items-center gap-0.5" data-testid={`wa-msg-${msg.message_id}-mobile-hint`}>
              <DeviceMobile size={11} /> Enviado desde WhatsApp Business
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LeadConversation({
  leadId,
  lead = null,
  unreadCount = 0,
  lastInboundAt = null,
  // Deep-link support (LeadDetailPage, see @/lib/leadDetailAnchors):
  // `preselectedTemplateId` loads that CRM suggestion into the composer
  // once (never sends); `autoFocusComposer` focuses the textarea once the
  // thread is ready and the 24h window is open.
  preselectedTemplateId = null,
  autoFocusComposer = false,
}) {
  // Rol REAL (no el viewMode): el enlace a la herramienta admin de
  // pendientes solo aparece para admin — la ruta destino ya está
  // protegida con RequireRole, esto solo evita mostrar un enlace muerto.
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [messages, setMessages] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | ready | error
  const [loadingOlder, setLoadingOlder] = useState(false);
  // Composer (Phase C)
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendNotice, setSendNotice] = useState(null); // {tone: 'error'|'warn'|'info', text}
  // "Insertar mensaje sugerido" — lazy: the catalog is only fetched the
  // first time the operator opens the picker (suggestEnabled stays true
  // afterwards; the list itself closes on lead change).
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestEnabled, setSuggestEnabled] = useState(false);
  // Meta template panel (Phase E) — the ONLY send path while the 24h
  // window is closed. Distinct catalog from the CRM suggested messages:
  // these are Meta-APPROVED templates, immutable, positional variables.
  const [tplPanelOpen, setTplPanelOpen] = useState(false);
  const [tplList, setTplList] = useState(null); // null = not fetched yet
  const [tplLoading, setTplLoading] = useState(false);
  const [tplError, setTplError] = useState(false);
  const [tplSelectedId, setTplSelectedId] = useState(null);
  const [tplVars, setTplVars] = useState([]);
  // After an uncertain/in-progress outcome the Send button locks for
  // this page session (draft preserved) — the backend blocks new ids
  // for the lead anyway; this just stops pointless attempts. Resets on
  // lead change only: "uncertain" cannot be safely auto-cleared here.
  const [sendBlocked, setSendBlocked] = useState(false);
  // WACO accepted-pending (202 pending_confirmation): the send was
  // ACCEPTED but the wamid arrives via webhook — the outbound bubble
  // appears in the thread only after the backend reconciles it. While
  // pending, sends stay blocked with an informative (non-error) notice;
  // the lock clears itself when a NEW outbound message shows up in the
  // poll (ids snapshotted below — unlike "uncertain", pending has a
  // well-defined resolution signal).
  const [pendingConfirm, setPendingConfirm] = useState(false);
  const pendingKnownIdsRef = useRef(null); // message_ids present when pending began
  // AI Reply Copilot (Fase 3C) — la IA solo rellena el composer; el envío
  // sigue siendo el botón/pipeline existente. aiNotice es informativo
  // (borrador generado / revisar / errores); aiClosedDraft es el borrador
  // en modo ventana cerrada (no hay textarea de texto libre ahí, así que
  // se muestra como texto seleccionable NO enviable).
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiNotice, setAiNotice] = useState(null); // {tone, text} | null
  const [aiHasDraft, setAiHasDraft] = useState(false);
  const [aiClosedDraft, setAiClosedDraft] = useState(null);
  // Espejo del último message_id para el race-guard del Copilot: la lista
  // `messages` capturada en el closure async queda obsoleta tras el await.
  const lastMsgIdRef = useRef(null);
  // Backend verdict overrides the local clock when Meta says 131047.
  const [windowForcedClosed, setWindowForcedClosed] = useState(false);
  const [unreadCleared, setUnreadCleared] = useState(false);
  const markedReadRef = useRef(false);
  const scrollRef = useRef(null);
  const composerRef = useRef(null);
  const prefillDoneRef = useRef(false);
  const autoFocusDoneRef = useRef(false);
  const stickToBottomRef = useRef(true);
  // Stale-request guard: bumped on every leadId change AND on unmount.
  // Every async path snapshots the generation before awaiting and
  // refuses to touch state if it moved — a slow response for Lead A can
  // never leak messages into Lead B's freshly mounted conversation.
  const genRef = useRef(0);

  const loadLatest = useCallback(async ({ initial = false } = {}) => {
    const gen = genRef.current;
    try {
      const { items, nextBefore: nb } = await getLeadWhatsAppMessages(leadId, { limit: PAGE_SIZE });
      if (genRef.current !== gen) return; // stale: lead changed / unmounted
      setMessages((prev) => (initial ? items : mergeById(prev, items)));
      if (initial) setNextBefore(nb);
      setPhase("ready");
    } catch {
      // Never surface backend payloads; a fixed message + retry is enough.
      if (genRef.current !== gen) return;
      if (initial) setPhase("error");
    }
  }, [leadId]);

  useEffect(() => {
    genRef.current += 1; // invalidate anything in flight for the old lead
    setMessages([]); setNextBefore(null); setPhase("loading");
    setDraft(""); setSending(false); setSendNotice(null); setSendBlocked(false);
    setWindowForcedClosed(false); setUnreadCleared(false); setSuggestOpen(false);
    // A template draft must NEVER survive a lead switch: sending lead
    // A's variables to lead B is exactly the bug this reset prevents.
    // tplLoading/tplError reset too — a fetch aborted mid-flight by the
    // genRef bump would otherwise leave tplLoading stuck true and the
    // openTemplatePanel guard would refuse to ever refetch. tplList is
    // kept: the catalog is lead-independent.
    setTplPanelOpen(false); setTplSelectedId(null); setTplVars([]);
    setTplLoading(false); setTplError(false);
    setPendingConfirm(false); pendingKnownIdsRef.current = null;
    // Un borrador IA jamás sobrevive un cambio de lead.
    setAiGenerating(false); setAiNotice(null); setAiHasDraft(false);
    setAiClosedDraft(null); lastMsgIdRef.current = null;
    markedReadRef.current = false;
    stickToBottomRef.current = true;
    loadLatest({ initial: true });
    return () => { genRef.current += 1; }; // unmount invalidates too
  }, [leadId, loadLatest]);

  // Reset the unread badge ONCE after the conversation actually loaded —
  // never on the 12s polls, and only when there was something to clear.
  useEffect(() => {
    if (phase !== "ready" || markedReadRef.current || unreadCount <= 0) return;
    markedReadRef.current = true;
    const gen = genRef.current;
    markLeadWhatsAppRead(leadId)
      .then(() => { if (genRef.current === gen) setUnreadCleared(true); })
      .catch(() => { /* badge simply stays; next visit retries */ });
  }, [phase, leadId, unreadCount]);

  // Modest polling, tab-visible only. Cleared on unmount / lead change.
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      loadLatest({ initial: false });
    };
    const id = setInterval(tick, POLL_MS);
    return () => clearInterval(id);
  }, [loadLatest]);

  // Un accepted-pending de WACO se resuelve cuando el webhook 'sent'
  // lo reconcilia en el backend y el outbound aparece por polling:
  // cualquier outbound NUEVO (ausente del snapshot tomado al entrar en
  // pending) limpia el aviso y desbloquea el envío. El backend sigue
  // siendo autoritativo: un intento prematuro responde 409 y el
  // bloqueo se re-arma.
  useEffect(() => {
    if (!pendingConfirm) return;
    const known = pendingKnownIdsRef.current;
    const reconciled = messages.some(
      (m) => m.direction === "outbound" && !(known && known.has(m.message_id)));
    if (reconciled) {
      setPendingConfirm(false);
      pendingKnownIdsRef.current = null;
      setSendBlocked(false);
      setSendNotice(null);
    }
  }, [messages, pendingConfirm]);

  // Keep the view pinned to the newest message unless the user scrolled
  // up (reading history); then leave their position alone.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };

  // 24h window — display/UX only; the backend is authoritative. Uses the
  // freshest inbound we know of: the lead field (from page load) or any
  // newer inbound that arrived through polling.
  const effectiveLastInbound = useMemo(() => {
    let latest = lastInboundAt || null;
    for (const m of messages) {
      if (m.direction === "inbound" && m.sent_at && (!latest || m.sent_at > latest)) {
        latest = m.sent_at;
      }
    }
    return latest;
  }, [messages, lastInboundAt]);

  const windowOpen = useMemo(() => {
    if (windowForcedClosed) return false;
    if (!effectiveLastInbound) return false;
    const t = Date.parse(effectiveLastInbound);
    return Number.isFinite(t) && Date.now() - t < WINDOW_MS;
  }, [effectiveLastInbound, windowForcedClosed]);

  // A genuinely new inbound reopens the window even if Meta had closed it.
  useEffect(() => { setWindowForcedClosed(false); }, [effectiveLastInbound]);

  // ---- AI Reply Copilot (Fase 3C) ------------------------------------

  useEffect(() => {
    lastMsgIdRef.current = messages.length
      ? messages[messages.length - 1].message_id
      : null;
  }, [messages]);

  // Modo según el estado real del hilo: último mensaje = inbound del lead
  // → REPLY (responder); si no → FOLLOW_UP (preparar seguimiento), con
  // presencia visual secundaria.
  const aiAwaitingReply = useMemo(() => {
    if (!messages.length) return false;
    return messages[messages.length - 1].direction === "inbound";
  }, [messages]);
  const aiMode = aiAwaitingReply ? "reply" : "follow_up";
  // Nunca pisar texto humano: con borrador no vacío el botón IA se
  // deshabilita (regla solo aplicable donde existe textarea libre).
  const aiBlockedByHumanText = windowOpen && !!draft.trim();

  const generateAi = async ({ regenerate = false } = {}) => {
    if (aiGenerating || sending || aiBlockedByHumanText) return;
    const gen = genRef.current;
    const lastIdAtClick = lastMsgIdRef.current;
    setAiGenerating(true);
    setAiNotice(null);
    try {
      const resp = await generateLeadAiReply(leadId, { mode: aiMode, regenerate });
      if (genRef.current !== gen) return; // lead cambió / unmount
      if (resp?.ok === false) {
        setAiNotice({ tone: "warn", text: "No hay mensajes de WhatsApp que responder todavía." });
        return;
      }
      // Race-guard local: si el hilo cambió mientras generábamos (nuevo
      // inbound por polling), el borrador respondería al mensaje anterior
      // — descartar y pedir regenerar. El backend aplica el mismo guard
      // server-side durante la generación.
      if (lastMsgIdRef.current !== lastIdAtClick) {
        setAiNotice({
          tone: "warn",
          text: "La conversación ha cambiado mientras se generaba. Vuelve a generar la respuesta.",
        });
        return;
      }
      const draftText = (resp?.draft || "").slice(0, MAX_TEXT);
      if (!draftText) {
        setAiNotice({ tone: "error", text: "No se pudo generar el borrador." });
        return;
      }
      if (windowOpen) {
        // El draft ENTRA EN EL COMPOSER: desde aquí es texto editable
        // normal y el envío sigue siendo el botón Enviar existente.
        setDraft(draftText);
      } else {
        setAiClosedDraft(draftText);
      }
      setAiHasDraft(true);
      setAiNotice(resp?.requires_human_review
        ? { tone: "warn", text: aiReviewText(resp?.review_reasons) }
        : { tone: "info", text: "Borrador generado con IA — revísalo antes de enviar." });
    } catch (e) {
      if (genRef.current !== gen) return;
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "ai_reply_context_changed" || code === "ai_reply_claim_lost") {
        setAiNotice({
          tone: "warn",
          text: "La conversación ha cambiado durante la generación. Vuelve a generar la respuesta.",
        });
      } else if (code === "ai_reply_in_progress") {
        setAiNotice({ tone: "info", text: "Ya se está generando una respuesta. Espera unos segundos." });
      } else if (code === "ai_not_configured") {
        setAiNotice({ tone: "warn", text: "La IA no está configurada en este entorno." });
      } else {
        setAiNotice({ tone: "error", text: "No se pudo generar el borrador." });
      }
    } finally {
      if (genRef.current === gen) setAiGenerating(false);
    }
  };

  // Suggested-message picker: same catalog + ordering the standalone
  // "Mensajes sugeridos" section uses, rendered with the lead's variables.
  const stage = normalizeStatus(lead?.status) || "new";
  const {
    templates: suggestTemplates,
    loading: suggestLoading,
    fallbackUsed: suggestUnavailable,
  } = useMessageTemplates({ channel: "whatsapp", enabled: suggestEnabled });
  const orderedSuggestions = useMemo(
    () => orderTemplatesForStage(suggestTemplates, stage),
    [suggestTemplates, stage],
  );

  const toggleSuggest = () => {
    if (!suggestEnabled) setSuggestEnabled(true);
    setSuggestOpen((o) => !o);
  };

  const insertSuggestion = (tpl) => {
    const rendered = renderWhatsappTemplate(tpl.body || "", lead || {});
    // Never destroy an existing draft: append after a blank line instead.
    setDraft((prev) => {
      const cur = prev.trimEnd();
      return (cur ? `${cur}\n\n${rendered}` : rendered).slice(0, MAX_TEXT);
    });
    setSuggestOpen(false);
  };

  // "Preparar mensaje" deep link: fetch the catalog and load the
  // recommended suggestion into the composer ONCE — same insert path as
  // the picker, so it only fills the textarea and never sends. Closed
  // window: nothing to prefill here (free text is not allowed; the
  // standalone "Mensajes sugeridos" card keeps the preselection).
  useEffect(() => {
    if (preselectedTemplateId) setSuggestEnabled(true);
  }, [preselectedTemplateId]);
  useEffect(() => {
    if (!preselectedTemplateId || prefillDoneRef.current) return;
    if (phase !== "ready") return;
    if (!windowOpen) { prefillDoneRef.current = true; return; }
    if (suggestLoading) return;
    if (suggestUnavailable) { prefillDoneRef.current = true; return; }
    if (!suggestTemplates.length) return;
    const tpl = suggestTemplates.find(
      (t) => t.template_id === preselectedTemplateId && t.is_active,
    );
    prefillDoneRef.current = true;
    if (!tpl) return;
    insertSuggestion(tpl);
    setSendNotice({ tone: "info", text: "Mensaje sugerido cargado — revísalo antes de enviar." });
    composerRef.current?.focus?.({ preventScroll: true });
    // insertSuggestion is stable for our purposes (closes over lead only).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedTemplateId, phase, windowOpen, suggestLoading, suggestUnavailable, suggestTemplates]);

  // "Abrir conversación" deep link: put the cursor in the composer once
  // the thread is ready (only meaningful while the window is open).
  useEffect(() => {
    if (!autoFocusComposer || autoFocusDoneRef.current) return;
    if (phase !== "ready") return;
    autoFocusDoneRef.current = true;
    if (windowOpen) composerRef.current?.focus?.({ preventScroll: true });
  }, [autoFocusComposer, phase, windowOpen]);

  // ---- Meta templates (closed window) --------------------------------

  const openTemplatePanel = async () => {
    setTplPanelOpen(true);
    if (tplList !== null || tplLoading) return; // fetched once per mount
    const gen = genRef.current;
    setTplLoading(true); setTplError(false);
    try {
      const templates = await getWhatsAppMetaTemplates();
      if (genRef.current !== gen) return;
      setTplList(templates);
    } catch {
      if (genRef.current !== gen) return;
      setTplError(true);
    } finally {
      if (genRef.current === gen) setTplLoading(false);
    }
  };

  const tplSelected = useMemo(
    () => (tplList || []).find((t) => t.template_id === tplSelectedId) || null,
    [tplList, tplSelectedId],
  );

  const selectTemplate = (tpl) => {
    // Re-clicking the already-selected row must NOT wipe typed values.
    if (tpl.template_id === tplSelectedId) return;
    setTplSelectedId(tpl.template_id);
    setTplVars(Array.from({ length: tpl.variable_count || 0 }, () => ""));
    // The uncertain-send warning ("no lo reenvíes todavía") stays on
    // screen while sendBlocked — it is the only visible explanation of
    // why the send buttons are disabled.
    if (!sendBlocked) setSendNotice(null);
  };

  // Local preview only — the backend renders/validates authoritatively.
  // Unfilled variables stay visible as {{n}} so the operator sees what
  // is still missing.
  const tplPreview = useMemo(() => {
    if (!tplSelected) return "";
    return (tplSelected.body || "").replace(/\{\{(\d+)\}\}/g, (m, n) => {
      const v = tplVars[Number(n) - 1];
      return v && v.trim() ? v : m;
    });
  }, [tplSelected, tplVars]);

  const tplComplete = !!tplSelected && tplVars.every((v) => v.trim());

  // WACO aceptó el envío pero el wamid llega por webhook: bloquear
  // reenvíos con lenguaje informativo (no de error) y recordar qué
  // mensajes ya existían para detectar el outbound reconciliado.
  const enterPendingConfirm = () => {
    pendingKnownIdsRef.current = new Set(messages.map((m) => m.message_id));
    setPendingConfirm(true);
    setSendBlocked(true);
    setSendNotice({
      tone: "info",
      text: "WACO aceptó el mensaje. Esperando confirmación de Meta. No lo reenvíes.",
    });
  };

  const sendTemplate = async () => {
    if (!tplComplete || sending || sendBlocked) return;
    const gen = genRef.current;
    setSending(true);
    setSendNotice(null);
    try {
      const resp = await sendLeadWhatsAppTemplate(leadId, {
        templateName: tplSelected.name,
        language: tplSelected.language,
        variables: tplVars.map((v) => v.trim()),
        clientRequestId: newClientRequestId(),
      });
      if (genRef.current !== gen) return;
      setTplPanelOpen(false); setTplSelectedId(null); setTplVars([]);
      if (resp?.pending_confirmation) {
        enterPendingConfirm();
        return;
      }
      stickToBottomRef.current = true;
      await loadLatest({ initial: false });
    } catch (e) {
      if (genRef.current !== gen) return;
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "whatsapp_send_pending_confirmation") {
        enterPendingConfirm(); // un envío anterior aceptado sigue pendiente
      } else if (code === "whatsapp_send_uncertain" || code === "whatsapp_send_in_progress") {
        setSendBlocked(true); // same contract as free-form: never resend blindly
        setSendNotice({
          tone: "warn",
          text: "No podemos confirmar si el mensaje se envió. No lo reenvíes todavía.",
        });
      } else if (code === "whatsapp_template_unavailable") {
        setSendNotice({ tone: "error", text: "La plantilla ya no está disponible." });
      } else if (code === "whatsapp_template_ambiguous") {
        setSendNotice({
          tone: "error",
          text: "La plantilla tiene varias variantes de idioma y no puede enviarse desde el CRM.",
        });
      } else {
        setSendNotice({ tone: "error", text: "No se pudo enviar la plantilla." });
      }
    } finally {
      if (genRef.current === gen) setSending(false);
    }
  };

  const sendMessage = async () => {
    const text = draft.trim();
    if (!text || sending || sendBlocked || text.length > MAX_TEXT) return;
    const gen = genRef.current;
    setSending(true);
    setSendNotice(null);
    try {
      await sendLeadWhatsAppMessage(leadId, {
        text, clientRequestId: newClientRequestId(),
      });
      if (genRef.current !== gen) return;
      setDraft("");
      // El aviso del Copilot pertenece al borrador ya enviado.
      setAiNotice(null);
      setAiHasDraft(false);
      stickToBottomRef.current = true;
      await loadLatest({ initial: false });
    } catch (e) {
      if (genRef.current !== gen) return;
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "whatsapp_template_required") {
        // Backend/Meta verdict wins over the local clock. Text preserved.
        setWindowForcedClosed(true);
      } else if (code === "whatsapp_send_pending_confirmation") {
        enterPendingConfirm(); // una plantilla aceptada sigue pendiente de wamid
      } else if (code === "whatsapp_send_uncertain" || code === "whatsapp_send_in_progress") {
        setSendBlocked(true); // draft preserved; no further request ids this session
        setSendNotice({
          tone: "warn",
          text: "No podemos confirmar si el mensaje se envió. No lo reenvíes todavía.",
        });
      } else {
        // Concise, fixed copy — backend payloads are never surfaced.
        setSendNotice({ tone: "error", text: "No se pudo enviar el mensaje." });
      }
    } finally {
      if (genRef.current === gen) setSending(false);
    }
  };

  const [retryingId, setRetryingId] = useState(null);
  const retryMedia = async (msg) => {
    if (retryingId) return;
    const gen = genRef.current;
    setRetryingId(msg.message_id);
    try {
      const resp = await retryLeadWhatsAppMedia(leadId, msg.message_id);
      if (genRef.current !== gen) return;
      if (resp?.media) {
        setMessages((prev) => prev.map((m) =>
          m.message_id === msg.message_id ? { ...m, media: resp.media } : m));
      }
    } catch {
      /* fixed notice stays; the button remains for another manual retry */
    } finally {
      if (genRef.current === gen) setRetryingId(null);
    }
  };

  const onComposerKeyDown = (e) => {
    // Enter inserts a newline (multiline messages are normal on
    // WhatsApp); only Cmd/Ctrl+Enter sends.
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      sendMessage();
    }
  };

  const loadOlder = async () => {
    if (!nextBefore || loadingOlder) return;
    const gen = genRef.current;
    setLoadingOlder(true);
    const el = scrollRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    try {
      const { items, nextBefore: nb } = await getLeadWhatsAppMessages(leadId, {
        limit: PAGE_SIZE, before: nextBefore,
      });
      if (genRef.current !== gen) return; // stale: lead changed / unmounted
      // Las páginas antiguas son historia: nunca contienen el outbound
      // reconciliado (siempre más nuevo que lo ya cargado). Se añaden
      // al snapshot del pending para que un outbound histórico no
      // desbloquee el aviso por error.
      if (pendingKnownIdsRef.current) {
        for (const m of items) pendingKnownIdsRef.current.add(m.message_id);
      }
      setMessages((prev) => mergeById(prev, items));
      setNextBefore(nb);
      stickToBottomRef.current = false;
      // Preserve the reading position across the prepend.
      requestAnimationFrame(() => {
        if (el) el.scrollTop = el.scrollHeight - prevHeight;
      });
    } catch {
      /* keep current page; the button stays available for retry */
    } finally {
      if (genRef.current === gen) setLoadingOlder(false);
    }
  };

  const body = useMemo(() => {
    if (phase === "loading") {
      return (
        <div className="py-10 text-center text-sm text-stone-400 animate-pulse" data-testid="wa-loading">
          Cargando conversación…
        </div>
      );
    }
    if (phase === "error") {
      return (
        <div className="py-10 text-center space-y-3" data-testid="wa-error">
          <div className="text-sm text-stone-500 dark:text-stone-400">
            Unable to load WhatsApp conversation.
          </div>
          <button
            type="button"
            className="dv-btn dv-btn-ghost text-xs inline-flex items-center gap-1"
            onClick={() => { setPhase("loading"); loadLatest({ initial: true }); }}
            data-testid="wa-retry"
          >
            <ArrowsClockwise size={13} /> Reintentar
          </button>
        </div>
      );
    }
    if (messages.length === 0) {
      return (
        <div className="py-10 text-center text-sm text-stone-400" data-testid="wa-empty">
          <ChatCircleDots size={22} className="mx-auto mb-1 opacity-60" />
          No hay mensajes de WhatsApp todavía.
        </div>
      );
    }
    return (
      <>
        {nextBefore && (
          <div className="text-center pb-2">
            <button
              type="button"
              onClick={loadOlder}
              disabled={loadingOlder}
              className="text-xs text-stone-500 hover:text-stone-800 dark:hover:text-stone-200 underline disabled:opacity-50"
              data-testid="wa-load-older"
            >
              {loadingOlder ? "Cargando…" : "Cargar mensajes anteriores"}
            </button>
          </div>
        )}
        <div className="space-y-2">
          {messages.map((m) => (
            <Bubble key={m.message_id} msg={m} onRetryMedia={retryMedia} retryingId={retryingId} />
          ))}
        </div>
      </>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, messages, nextBefore, loadingOlder, retryingId]);

  return (
    <div className="dv-card p-0 overflow-hidden" data-testid="lead-conversation">
      <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200 dark:border-stone-700">
        <div className="flex items-center gap-2 font-medium text-sm">
          <WhatsappLogo size={16} weight="bold" className="text-emerald-600" />
          WhatsApp
        </div>
        {unreadCount > 0 && !unreadCleared && (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800"
            data-testid="wa-unread-badge"
          >
            {unreadCount} sin leer
          </span>
        )}
      </div>

      {/* WhatsApp is a primary operational surface: give the thread real
          vertical working space (~420px mobile / ~560px desktop) while
          keeping internal scroll + stick-to-bottom. Width untouched. */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="min-h-[180px] max-h-[420px] md:max-h-[560px] overflow-y-auto px-4 py-3 bg-stone-50 dark:bg-stone-950"
        data-testid="wa-scroll"
      >
        {body}
      </div>

      {/* Composer (Phase C) — free-form text inside the 24h window.
          Backend re-validates everything: role, window, idempotency. */}
      <div className="border-t border-stone-200 dark:border-stone-700 px-4 py-3 space-y-2">
        {sendNotice && (
          <div
            className={`text-xs rounded px-3 py-2 border ${
              sendNotice.tone === "warn"
                ? "text-amber-800 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-950/40 dark:border-amber-800"
                : sendNotice.tone === "info"
                ? "text-emerald-800 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-800"
                : "text-red-700 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-950/40 dark:border-red-800"
            }`}
            data-testid="wa-send-notice"
          >
            {sendNotice.text}
            {/* Fase 1.2: acceso discreto a la herramienta admin de
                resolución — solo mientras hay un pending que bloquea y
                solo para admin. La herramienta completa NO vive aquí. */}
            {pendingConfirm && isAdmin && (
              <div className="mt-1">
                <Link
                  to="/admin/whatsapp-pendientes"
                  className="underline text-[11px] opacity-80 hover:opacity-100"
                  data-testid="wa-pending-review-link"
                >
                  Revisar envío pendiente
                </Link>
              </div>
            )}
          </div>
        )}
        {aiNotice && (
          <div
            className={`text-xs rounded px-3 py-2 border ${
              aiNotice.tone === "warn"
                ? "text-amber-800 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-950/40 dark:border-amber-800"
                : aiNotice.tone === "info"
                ? "text-emerald-800 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-800"
                : "text-red-700 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-950/40 dark:border-red-800"
            }`}
            data-testid="wa-ai-notice"
          >
            {aiNotice.text}
          </div>
        )}
        {windowOpen ? (
          <>
            {/* Sugerencias CRM (no son plantillas Meta): sólo rellenan el
                textarea — el envío sigue siendo siempre manual. IA Copilot
                (3C): también SOLO rellena el textarea; el envío es el
                mismo botón Enviar de siempre. */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <button
                type="button"
                onClick={toggleSuggest}
                className="text-[11px] text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 inline-flex items-center gap-1"
                data-testid="wa-suggest-toggle"
              >
                <ChatText size={12} /> Insertar mensaje sugerido
                {suggestOpen ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />}
              </button>
              <div className="inline-flex items-center gap-2">
                {aiHasDraft && !aiGenerating && (
                  <button
                    type="button"
                    onClick={() => generateAi({ regenerate: true })}
                    disabled={aiBlockedByHumanText || sending}
                    className="text-[11px] text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 underline disabled:opacity-50 disabled:no-underline"
                    title={aiBlockedByHumanText
                      ? "Vacía el borrador actual para generar una respuesta con IA."
                      : "Genera un borrador nuevo (nueva llamada IA)"}
                    data-testid="wa-ai-regenerate"
                  >
                    Regenerar
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => generateAi()}
                  disabled={aiGenerating || sending || aiBlockedByHumanText}
                  className={aiAwaitingReply
                    ? "dv-btn dv-btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-50"
                    : "text-[11px] text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 inline-flex items-center gap-1 disabled:opacity-50"}
                  title={aiBlockedByHumanText
                    ? "Vacía el borrador actual para generar una respuesta con IA."
                    : ""}
                  data-testid="wa-ai-generate"
                >
                  <Sparkle size={13} weight="fill" />
                  {aiGenerating
                    ? "Preparando respuesta…"
                    : aiAwaitingReply
                    ? "Generar respuesta con IA"
                    : "Preparar seguimiento con IA"}
                </button>
              </div>
            </div>
            {suggestOpen && (
              <div
                className="rounded-md border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 max-h-44 overflow-y-auto divide-y divide-stone-100 dark:divide-stone-800"
                data-testid="wa-suggest-list"
              >
                {suggestLoading ? (
                  <div className="px-3 py-2 text-xs text-stone-400 italic">Cargando sugerencias…</div>
                ) : suggestUnavailable || orderedSuggestions.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-stone-400 italic">
                    No hay mensajes sugeridos disponibles.
                  </div>
                ) : (
                  orderedSuggestions.map((t) => {
                    const recommended = t.stage === stage && t.is_default_for_stage;
                    return (
                      <button
                        key={t.template_id}
                        type="button"
                        onClick={() => insertSuggestion(t)}
                        className="block w-full text-left px-3 py-2 hover:bg-stone-50 dark:hover:bg-stone-800"
                        data-testid={`wa-suggest-${t.template_id}`}
                      >
                        <div className="text-xs font-medium text-stone-700 dark:text-stone-200">
                          {t.name}
                          {recommended && (
                            <span className="ml-1.5 text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400">
                              Recomendada
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-stone-400 truncate">
                          {renderWhatsappTemplate(t.body || "", lead || {})}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            )}
            <div className="flex items-end gap-2">
              <textarea
                ref={composerRef}
                rows={2}
                className="dv-input flex-1 text-sm resize-none"
                placeholder="Escribe una respuesta…"
                value={draft}
                maxLength={MAX_TEXT}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onComposerKeyDown}
                disabled={sending || aiGenerating}
                data-testid="wa-composer-input"
              />
              <button
                type="button"
                onClick={sendMessage}
                disabled={sending || sendBlocked || !draft.trim()}
                className="dv-btn dv-btn-primary shrink-0 inline-flex items-center gap-1 text-sm disabled:opacity-50"
                title={sendBlocked ? "Hay un envío sin confirmar para este lead" : "Cmd/Ctrl+Enter para enviar"}
                data-testid="wa-composer-send"
              >
                <PaperPlaneRight size={14} weight="bold" />
                {sending ? "Enviando…" : "Enviar"}
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-2">
            {/* Ventana cerrada: SIN texto libre. La única vía es una
                plantilla Meta aprobada — nunca los mensajes sugeridos
                del CRM (esos son texto libre y viven solo en la rama
                de ventana abierta). */}
            <div
              className="text-xs text-stone-500 dark:text-stone-400 italic"
              data-testid="wa-window-closed"
            >
              La ventana de 24 h está cerrada. Solo se pueden enviar
              plantillas de WhatsApp aprobadas.
            </div>
            {/* Copilot con ventana cerrada: puede generar el borrador, pero
                NUNCA lo convierte en enviable — aquí no hay texto libre, y
                el único camino de envío sigue siendo la plantilla Meta. */}
            <div>
              <button
                type="button"
                onClick={() => generateAi()}
                disabled={aiGenerating || sending}
                className="text-[11px] text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="wa-ai-generate"
              >
                <Sparkle size={13} weight="fill" />
                {aiGenerating ? "Preparando mensaje…" : "Preparar mensaje con IA"}
              </button>
            </div>
            {aiClosedDraft && (
              <div
                className="rounded-md border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-3 space-y-1.5"
                data-testid="wa-ai-closed-draft"
              >
                <div className="text-[10px] uppercase tracking-widest text-stone-500">
                  Borrador IA
                </div>
                <div className="text-sm text-stone-700 dark:text-stone-200 whitespace-pre-wrap break-words select-text">
                  {aiClosedDraft}
                </div>
                <div className="text-[11px] text-amber-700 dark:text-amber-400">
                  La ventana de WhatsApp está cerrada. Este borrador no puede
                  enviarse como texto libre; necesitarás una plantilla aprobada.
                </div>
              </div>
            )}
            {!tplPanelOpen ? (
              <button
                type="button"
                onClick={openTemplatePanel}
                className="dv-btn dv-btn-primary text-xs inline-flex items-center gap-1.5"
                data-testid="wa-template-open"
              >
                <PaperPlaneRight size={13} weight="bold" /> Enviar plantilla de WhatsApp
              </button>
            ) : (
              <div
                className="rounded-md border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-3 space-y-2.5"
                data-testid="wa-template-panel"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-widest text-stone-500">
                    Plantillas de WhatsApp (Meta)
                  </span>
                  <button
                    type="button"
                    onClick={() => { setTplPanelOpen(false); setTplSelectedId(null); setTplVars([]); }}
                    className="text-[11px] text-stone-500 hover:text-stone-800 dark:hover:text-stone-200"
                    data-testid="wa-template-cancel"
                  >
                    Cancelar
                  </button>
                </div>

                {tplLoading ? (
                  <div className="text-xs text-stone-400 italic">Cargando plantillas…</div>
                ) : tplError ? (
                  <div className="text-xs text-stone-400 italic">
                    No se pudieron cargar las plantillas.{" "}
                    <button type="button" onClick={() => { setTplList(null); setTplError(false); openTemplatePanel(); }}
                            className="underline not-italic">Reintentar</button>
                  </div>
                ) : (tplList || []).length === 0 ? (
                  <div className="text-xs text-stone-400 italic" data-testid="wa-template-empty">
                    No hay plantillas aprobadas registradas todavía.
                  </div>
                ) : (
                  <div className="rounded border border-stone-200 dark:border-stone-700 max-h-40 overflow-y-auto divide-y divide-stone-100 dark:divide-stone-800">
                    {(tplList || []).map((t) => (
                      <button
                        key={t.template_id}
                        type="button"
                        onClick={() => selectTemplate(t)}
                        className={`block w-full text-left px-3 py-2 ${
                          t.template_id === tplSelectedId
                            ? "bg-stone-100 dark:bg-stone-800"
                            : "hover:bg-stone-50 dark:hover:bg-stone-800"
                        }`}
                        data-testid={`wa-template-${t.template_id}`}
                      >
                        <div className="text-xs font-medium text-stone-700 dark:text-stone-200">
                          {t.name}
                          {t.language && (
                            <span className="ml-1.5 text-[10px] uppercase tracking-widest text-stone-400">
                              {t.language}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-stone-400 truncate">{t.body}</div>
                      </button>
                    ))}
                  </div>
                )}

                {tplSelected && (
                  <>
                    {(tplSelected.variable_count || 0) > 0 && (
                      <div className="space-y-1.5">
                        {tplVars.map((v, i) => (
                          <label key={i} className="block text-[11px] text-stone-500">
                            {(tplSelected.variable_hints || [])[i] || `Variable {{${i + 1}}}`}
                            <input
                              type="text"
                              className="dv-input mt-0.5 w-full text-sm"
                              value={v}
                              maxLength={200}
                              onChange={(e) => setTplVars((prev) =>
                                prev.map((x, j) => (j === i ? e.target.value : x)))}
                              disabled={sending}
                              data-testid={`wa-template-var-${i + 1}`}
                            />
                          </label>
                        ))}
                      </div>
                    )}
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-0.5">
                        Vista previa
                      </div>
                      <div
                        className="text-sm text-stone-700 dark:text-stone-200 whitespace-pre-wrap bg-stone-50 dark:bg-stone-950 border border-stone-100 dark:border-stone-800 rounded p-2"
                        data-testid="wa-template-preview"
                      >
                        {tplPreview}
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={sendTemplate}
                        disabled={sending || sendBlocked || !tplComplete}
                        className="dv-btn dv-btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-50"
                        title={sendBlocked
                          ? "Hay un envío sin confirmar para este lead"
                          : tplComplete ? "" : "Completa todas las variables"}
                        data-testid="wa-template-send"
                      >
                        <PaperPlaneRight size={13} weight="bold" />
                        {sending ? "Enviando…" : "Enviar plantilla"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
